/*
  The rules of a feedback draft, with no Vue and no DOM. The widget owns the state and the
  markup; this module owns the decisions, so they can be tested against their boundaries
  rather than through a rendered dialog.
*/

import { countCharacters } from './countCharacters'

export const REQUEST_TYPES = ['Problem', 'Question', 'Documentation', 'Suggestion'] as const

export type RequestType = (typeof REQUEST_TYPES)[number]

export const MAX_SUMMARY = 120
export const MAX_DETAILS = 2000

export type FeedbackDraft = {
  requestType: RequestType
  summary: string
  details: string
}

export const EMPTY_DRAFT: FeedbackDraft = {
  requestType: REQUEST_TYPES[0],
  summary: '',
  details: ''
}

export type DraftField = 'summary' | 'details'

/* The order a keyboard user meets the fields, which is also the order to report them in. */
export const REQUIRED_FIELDS: DraftField[] = ['summary', 'details']

export type DraftErrors = Partial<Record<DraftField, string>>

export type ClampResult = {
  value: string
  dropped: number
}

/*
  A notice that the interface owes the user: this field lost this many characters. Held
  separately from the value, because the value alone cannot say what used to be there.
*/
export type DropNotice = {
  field: DraftField
  dropped: number
} | null

export function newDraft(): FeedbackDraft {
  return { ...EMPTY_DRAFT }
}

/*
  The counting basis for every limit in this module: Unicode code points, not the UTF-16
  code units that String.length and String.slice work in. An emoji or a supplementary-plane
  letter is stored as a surrogate pair, so a code-unit slice can cut between the two halves
  and leave a lone surrogate behind. That is not a cosmetic fault: encodeMailComponent in
  feedbackMail calls encodeURIComponent, which throws 'URI malformed' on a lone surrogate,
  and the throw took the whole 'Prepare email' action down with no message to the user.
  Counting in code units also made one dropped emoji report as two dropped characters, so
  the notice the interface announced was untrue.

  The function itself now lives in countCharacters, because the CharacterCount component has
  to show a number measured on this same basis and a shared component must not reach into a
  feature module to get it. It is re-exported here so this module still declares, in one
  place, the basis every limit below is measured on.
*/
export { countCharacters }

/*
  Keeps the leading slice and reports the exact loss. The count is what the interface
  announces, so it must be the real number of characters removed, not a rounded one.
*/
export function clampWithNotice(value: string, max: number): ClampResult {
  const characters = Array.from(value)

  if (characters.length <= max) {
    return { value, dropped: 0 }
  }

  return { value: characters.slice(0, max).join(''), dropped: characters.length - max }
}

/*
  What the drop notice becomes after one more edit. Two rules make it honest:
  the largest loss on a field survives later small losses, because the user needs to know
  the whole amount; and the notice clears as soon as the field falls back under its ceiling,
  because at that point nothing is being lost any more.

  The comparison against max counts code points, because that is the basis clampWithNotice
  clamped on. Measuring the clamped value in code units instead would read a field holding
  exactly max emoji as being over its ceiling, and the notice would never clear.
*/
export function nextDropNotice(
  current: DropNotice,
  field: DraftField,
  clamped: ClampResult,
  max: number
): DropNotice {
  if (countCharacters(clamped.value) < max) {
    return current?.field === field ? null : current
  }

  if (clamped.dropped === 0) {
    return current
  }

  if (current?.field === field) {
    return { field, dropped: Math.max(current.dropped, clamped.dropped) }
  }

  return { field, dropped: clamped.dropped }
}

/*
  Whether a dismissal would destroy something the user wrote. The request type has a default
  and carries no work, so changing it alone must not trigger a confirmation.
*/
export function hasUnsavedWork(draft: FeedbackDraft): boolean {
  return draft.summary.trim().length > 0 || draft.details.trim().length > 0
}

/*
  Whitespace is not an answer, so a field holding only spaces counts as missing. These two
  functions and hasUnsavedWork ask only whether text is present, and a string is empty in
  code units exactly when it is empty in code points, so they need no counting basis of
  their own. Only a comparison against a limit does.
*/
export function validateDraft(draft: FeedbackDraft): DraftErrors {
  const errors: DraftErrors = {}

  if (!draft.summary.trim()) {
    errors.summary = 'Enter a short summary of your request.'
  }

  if (!draft.details.trim()) {
    errors.details = 'Describe what happened, so the support team can act on it.'
  }

  return errors
}

export function firstInvalidField(errors: DraftErrors): DraftField | undefined {
  return REQUIRED_FIELDS.find((field) => Boolean(errors[field]))
}
